import React, { useContext, useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { routes, userRoles } from "../../../../../constants";
import { useLocation, useNavigate } from "react-router-dom";
import { Form, Input, Typography, Space, Button, Flex } from "antd";
import { login } from "../../../../../redux/services/auth/authServices";
import {
  baseRouteMaker,
  baseRouteMakerForSuperAdmin,
} from "../../../../../constants/routeConsts";
import { EyeInvisibleOutlined, EyeTwoTone } from "@ant-design/icons";
import RequiredLabel from "../../../../common/required-label/RequiredLabel";
import AgeVerficationModal from "./AgeVerficationModal";
import { colors } from "../../../../../constants/colors";
import { sanitizeDataTypeClientSide } from "../../../../../utils/utils";
import useUserToken from "../../../../../hooks/useToken";
import useLocalStorage from "../../../../../hooks/useLocalStorage";
import useAuthenticate from "../../../../../hooks/useAuthenticate";
import { appConfig } from "../../../../../constants/config/appConfig";
import { useEffect } from "react";
import styles from "./SignIn.module.scss";
import { LanguageContext } from "../../../../../context/LanguageProvider";

const SignIn = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const [loading, setLoading] = useState(false);

  const [openServiceDeleteDialog, setOpenServiceDeleteDialog] = useState(false);

  /* token dependencies */
  const [token, setUserToken] = useUserToken(appConfig.SESSION_USER_TOKEN);
  /* token dependencies */

  const { updateLanguage } = useContext(LanguageContext);

  /* local storage dependencies */
  const { userSession, setUserSession } = useLocalStorage(
    appConfig.SESSION_USER_INFO
  );
  /* local storage dependencies */

  const isAuthenticated = useAuthenticate();

  useEffect(() => {
    if (isAuthenticated && userSession && userSession?.userRole) {
      const role = userSession?.userRole;
      role === userRoles.SUPER_ADMIN
        ? navigate(baseRouteMakerForSuperAdmin("/"))
        : navigate(baseRouteMaker("/"));
    }
  }, []);

  const onFinish = (values) => {
    if (values) {
      let loginBody = {
        emailOrPhoneNumber: values.emailOrPhoneNumber,
        password: values.password,
      };
      dispatch(
        login(
          loginBody,
          navigate,
          setLoading,
          setUserToken,
          setUserSession,
          updateLanguage
        )
      );
    }
  };

  return (
    <>
      <Flex vertical gap="1rem" className={styles.signInContainer}>
        <Typography.Title level={1} style={{ color: colors.primaryColor ,fontSize:'34px'}}>
          {t("SignIn.title")}
        </Typography.Title>
        <Typography.Title level={4} type="secondary" style={{ margin: 0 }}>
          {t("SignIn.subTitle")}
        </Typography.Title>
        <Form
          layout="vertical"
          onFinish={onFinish}
          autoComplete="off"
          requiredMark={false}
        >
          <Flex vertical gap="4rem">
            <div>
              <Form.Item
                label={
                  <RequiredLabel
                    text={t("SignIn.emailLabel")}
                    required={true}
                  />
                }
                name="emailOrPhoneNumber"
                rules={[
                  {
                    required: true,
                    message: t("SignIn.emailErrorMessage"),
                  },
                  {
                    whitespace: true,
                    message: t("SignIn.emailErrorMessage"),
                  },
                ]}
                normalize={(value) => sanitizeDataTypeClientSide(value)}
              >
                <Input placeholder={t("SignIn.emailPlaceholder")} />
              </Form.Item>
              <Form.Item
                label={
                  <RequiredLabel
                    text={t("SignIn.passwordLabel")}
                    required={true}
                  />
                }
                name="password"
                rules={[
                  {
                    required: true,
                    message: t("SignIn.passwordErrorMessage"),
                  },
                  {
                    validator: (_, value) => {
                      if (value && /\s/.test(value)) {
                        return Promise.reject(
                          t("SignIn.passwordNoSpaceErrorMessage")
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
                normalize={(value) => sanitizeDataTypeClientSide(value)}
              >
                <Input.Password
                  placeholder={t("SignIn.passwordPlaceholder")}
                  iconRender={(visible) =>
                    visible ? (
                      <EyeTwoTone style={{ fontSize: 16 }} />
                    ) : (
                      <EyeInvisibleOutlined style={{ fontSize: 16 }} />
                    )
                  }
                />
              </Form.Item>
              <Typography.Title
                level={5}
                type="success"
                style={{ cursor: "pointer" }}
                onClick={() => navigate(baseRouteMaker(routes.FORGOT_PASSWORD))}
              >
                {t("SignIn.forgotPassword")}
              </Typography.Title>
            </div>

            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
               style={{ width: "100%" }}
            >
              {t("SignIn.login")}
            </Button>
          </Flex>
        </Form>
        <Space>
          <Typography.Title level={5}>{t("SignIn.noAccount")}</Typography.Title>
          <Typography.Title
            level={5}
            type="success"
            style={{ cursor: "pointer" }}
            onClick={() => {
              navigate(baseRouteMaker(routes.SIGN_UP))
            }}
          >
            {t("SignIn.createAcount")}
          </Typography.Title>
        </Space>
      </Flex>
    </>
  );
};
export default SignIn;
